import type { ReactNode } from 'react'
import { useState } from 'react'
import { AlertBanner, Button, Group, Stack, Text } from '@grovus/design-system'
import { fill, money } from './format'
import type { Row } from './partition'
import type { Totals } from './partition'
import type { ViewContext } from './view'

export interface Bucket {
  axis: 'hypothesis' | 'technique' | 'audience'
  id: string
}

// A note row does not wrap, so a flexible button is squeezed below its label at narrow
// widths; the button takes the width its text needs and the note text wraps instead.
const NOTE_ACTION_STYLE = { whiteSpace: 'nowrap', flex: 'none' } as const

interface NotesProps {
  ctx: ViewContext
  /** Shown while the bucket is the only narrowing set; it stands down on a second filter. */
  bucket: Bucket | null
  bucketLabel: string | null
  bucketCount: number
  bucketAlone: boolean
  onBucketBack: () => void
  focus: 'scale' | 'kill' | null
  focusMatches: number
  focusTotal: number
  hidden: Row[]
  hiddenSpend: number
  /** Every creative in the segment, which the tiles describe and the floor does not change. */
  segmentTotal: number
  onClearFloor: () => void
}

/**
 * The three quiet rows under the filter bar, each rendered only when it is true.
 *
 * They exist because a figure on this screen can be narrowed in ways the figure itself
 * cannot show: arriving from a hypothesis comparison, arriving from a Question, or a floor
 * the reader set and forgot. Each note says what was done and offers the way back.
 */
export function Notes({
  ctx, bucket, bucketLabel, bucketCount, bucketAlone, onBucketBack,
  focus, focusMatches, focusTotal, hidden, hiddenSpend, segmentTotal, onClearFloor,
}: NotesProps) {
  const { t } = ctx
  const axisWord = bucket
    ? { hypothesis: t.axisHyp, technique: t.axisVisual, audience: t.axisAud }[bucket.axis]
    : ''

  const rows: Array<{ key: string; text: string; action?: ReactNode }> = []

  if (bucket && bucketAlone && bucketLabel) {
    rows.push({
      key: 'bucket',
      text: fill(t.bucketNote, { v: bucketLabel, c: bucketCount, a: axisWord }),
      action: (
        <Button variant="default" onClick={onBucketBack} style={NOTE_ACTION_STYLE}>
          {t.bucketBack}
        </Button>
      ),
    })
  }

  if (focus) {
    const verdictWord = focus === 'scale' ? t.focusScale : t.focusKill
    rows.push({
      key: 'focus',
      text:
        focusMatches > 0
          ? fill(t.focusSome, { n: focusMatches, t: focusTotal, v: verdictWord })
          : fill(t.focusNone, { v: verdictWord }),
    })
  }

  if (hidden.length > 0) {
    rows.push({
      key: 'floor',
      text: fill(t.floorNote, {
        c: hidden.length,
        s: money(hiddenSpend, ctx.currency, ctx.lang),
        t: segmentTotal,
      }),
      action: (
        <Button variant="default" onClick={onClearFloor} style={NOTE_ACTION_STYLE}>
          {t.floorClear}
        </Button>
      ),
    })
  }

  if (rows.length === 0) return null

  return (
    <Stack gap={6} data-testid="creatives-notes">
      {rows.map((row) => (
        <Group key={row.key} justify="space-between" wrap="nowrap" gap="md">
          <Text size="xs">{row.text}</Text>
          {row.action}
        </Group>
      ))}
    </Stack>
  )
}

interface ThinBannerProps {
  ctx: ViewContext
  totals: Totals
  onCompareHypotheses: () => void
}

/**
 * The banner that stops a reader trusting a table that looks fine.
 *
 * When most of a segment sits under the provisional line, the individual rows carry no
 * rankable ratio however confident the column looks, and pooling by hypothesis is the only
 * honest way to read them. It speaks in the informational tone since GRO-63: a thin
 * sample is a statistical condition, not an error, and red stays reserved for problem
 * states. The dropped tail - why the window cannot rank these rows - moved into the
 * rankable tile's mark and the guide, reworded so no reusable text hardcodes a window.
 */
export function ThinBanner({ ctx, totals, onCompareHypotheses }: ThinBannerProps) {
  const { t } = ctx
  if (totals.thin === 0) return null
  return (
    <div data-testid="creatives-thin">
      <AlertBanner
        tone="info"
        action={
          // The Russian label is longer than the English one and was being clipped at
          // 1440px; the button takes the width its text needs and the message wraps.
          <Button onClick={onCompareHypotheses} style={{
            whiteSpace: 'nowrap', flex: 'none',
            borderColor: 'light-dark(transparent, var(--mantine-color-brand-3))',
            outlineColor: 'light-dark(var(--mantine-primary-color-filled), var(--mantine-color-brand-3))',
          }}>
            {t.compareHypo}
          </Button>
        }
      >
        {fill(t.bannerThin, {
          c: totals.thin,
          s: money(totals.thinSpend, ctx.currency, ctx.lang),
        })}
      </AlertBanner>
    </div>
  )
}

/**
 * How to read this table: one quiet disclosure, closed by default.
 *
 * Each explanation also lives on its column's mark (D2), so the guide is the fallback a
 * reader opens once and stops needing - which is why it costs one line closed. The entries
 * render only while open: a closed disclosure mounts no content, which is what the tests
 * and the browser gates must expand before asserting against. Placement, decided at the
 * design checkpoint: between the banner and the table, where the legend sits today.
 * Composed from the existing design-system exports; the InfoMark is this task's reusable
 * control, and a one-item disclosure is screen furniture, not a design-system surface.
 */
export function Definitions({ ctx }: { ctx: ViewContext }) {
  const { t } = ctx
  const [open, setOpen] = useState(false)
  const accountNames = ctx.accounts.map((account) => account.short).join(', ')
  const entries: Array<[string, string]> = [
    [t.defNLead, t.defNBody],
    [t.defAdjLead, t.defAdjPooledBody],
    [t.defStruckLead, fill(t.defStruckBody, { n: ctx.thresholds.directionAt })],
    [t.defFunLead, t.defFunBody],
    // Marked once, on the definition, rather than once per account name: the band is
    // what is computed from demo figures, and repeating the marker beside three shorts
    // would say the same thing three times.
    [ctx.isDemo ? `${t.defBandLead} · ${t.demoMark}` : t.defBandLead, fill(t.defBandBody, { a: accountNames })],
    [t.defShapeLead, t.defShapeBody],
  ]
  return (
    <Stack gap={6} data-testid="creatives-definitions">
      <Button
        variant="default"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-controls="creatives-definitions-body"
        data-testid="creatives-definitions-toggle"
        style={{ alignSelf: 'flex-start' }}
      >
        {t.howToRead}
      </Button>
      {open && (
        <Stack gap={4} id="creatives-definitions-body">
          {entries.map(([lead, body]) => (
            <Text key={lead} size="xs" className="grv-muted">
              <Text component="span" size="xs" fw={700}>
                {lead}
              </Text>{' '}
              {body}
            </Text>
          ))}
        </Stack>
      )}
    </Stack>
  )
}
